// Phase H+1 Mini-Wave 5 · V12 User Topbar · credit badge persistente.
// V12 Achromatic Strict · italiano puro default.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, ChevronRight, Command, Compass, Coins, LogOut } from "lucide-react";
import { toast } from "sonner";

import V12LanguageToggle from "@/components/admin_v12/V12LanguageToggle";
import V12UserAvatarMenu from "@/components/user_v12/V12UserAvatarMenu";
import UsageWidget from "@/components/user_v12/UsageWidget";
// Wave IX.A · AI Economy · reputation badge (consumes /api/v2/ai-economy/reputation/me)
import ReputationBadge from "@/components/user/ReputationBadge";
import { useTranslation } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";

export default function V12UserTopbar({
    pageTitle = "Dashboard",
    onOpenMega,
    onOpenCmd,
    onOpenNotif,
    notificationsCount = 0,
}) {
    const { t } = useTranslation();
    const { user, logout } = useAuth();
    // Credit badge · fonte reale: AuthContext.user.credits (popolato da /api/auth/me).
    // F92 (Phase I Wave 1.29) · subscribe to `credits:changed` CustomEvent so
    // the badge updates instantly after any credit-consuming operation
    // (lead search, outreach send, AI task, …) without waiting for the next
    // /api/auth/me poll.
    const [liveCredits, setLiveCredits] = useState(null);
    useEffect(() => {
        function _handler(e) {
            const d = e?.detail || {};
            if (typeof d.new_balance === "number") setLiveCredits(d.new_balance);
            if (typeof d.consumed === "number" && d.consumed > 0) {
                toast.info(`-${d.consumed} ${d.consumed === 1 ? "credito" : "crediti"}`);
            }
        }
        window.addEventListener("credits:changed", _handler);
        return () => window.removeEventListener("credits:changed", _handler);
    }, []);
    const displayCredits = (liveCredits !== null)
        ? liveCredits
        : (user && typeof user.credits === "number")
            ? user.credits
            : "—";

    return (
        <header
            className="sticky top-0 z-20 h-14 flex items-center gap-3 px-4
                       border-b v12-border v12-topbar-bg backdrop-blur"
            data-testid="v12-user-topbar"
        >
            {/* 1 · Logo + breadcrumb */}
            <div className="flex items-center gap-2 min-w-0">
                <Link
                    to="/app/dashboard"
                    className="text-sm font-semibold tracking-tight
                               text-neutral-100 whitespace-nowrap"
                    data-testid="v12-user-topbar-logo"
                >
                    GlobalLeadSuite
                </Link>
                <ChevronRight size={12} className="stroke-zinc-600 shrink-0" />
                <span
                    className="text-xs text-neutral-400 truncate"
                    data-testid="v12-user-topbar-breadcrumb"
                >
                    {pageTitle}
                </span>
            </div>

            {/* 2 · Mega-menu "Vai a..." */}
            <button
                type="button"
                onClick={onOpenMega}
                className="ml-4 inline-flex items-center gap-2 text-xs
                           px-2.5 py-1.5 rounded-md border v12-border
                           v12-card text-neutral-300
                           hover:border-[#CCCCCC]/30 hover:text-neutral-100
                           transition-colors duration-200"
                data-testid="v12-user-topbar-mega-button"
            >
                <Compass size={14} className="stroke-zinc-400" />
                {t("layout.go_to")}
            </button>

            {/* 3 · ⌘K command palette */}
            <button
                type="button"
                onClick={onOpenCmd}
                className="inline-flex items-center gap-2 text-xs
                           px-2.5 py-1.5 rounded-md border v12-border
                           v12-card text-neutral-400
                           hover:border-[#CCCCCC]/30 hover:text-neutral-100
                           transition-colors duration-200"
                data-testid="v12-user-topbar-cmdk-button"
                title={t("layout.search_placeholder")}
            >
                <Command size={14} className="stroke-zinc-500" />
                {t("common.search")}
                <kbd className="ml-1 px-1.5 py-0.5 rounded border v12-border
                                text-[10px] text-neutral-500">⌘K</kbd>
            </button>

            <div className="ml-auto flex items-center gap-2">
                {/* 4a · Credit badge persistente (CTA conversion) */}
                <Link
                    to="/app/credits"
                    className="inline-flex items-center gap-1.5 text-xs
                               px-2.5 py-1.5 rounded-md border v12-border
                               v12-card text-neutral-200
                               v12-hover hover:border-[#CCCCCC]/30
                               hover:text-white
                               transition-colors duration-200"
                    data-testid="v12-user-topbar-credit-badge"
                    title={t("user_dashboard.credits_badge_tooltip") !== "user_dashboard.credits_badge_tooltip"
                        ? t("user_dashboard.credits_badge_tooltip")
                        : "I tuoi crediti disponibili"}
                >
                    <Coins size={14} className="stroke-zinc-400" />
                    <span className="tabular-nums font-medium">{displayCredits}</span>
                </Link>

                {/* Wave IX.A · reputation badge (AI Economy) */}
                <ReputationBadge />

                {/* 4b · Language toggle */}
                <V12LanguageToggle />

                {/* 5a · Notifiche */}
                <button
                    type="button"
                    onClick={onOpenNotif}
                    className="relative inline-flex items-center justify-center
                               w-9 h-9 rounded-md border v12-border
                               v12-card text-neutral-300
                               hover:border-[#CCCCCC]/30 hover:text-neutral-100
                               transition-colors duration-200"
                    data-testid="v12-user-topbar-notif-button"
                    aria-label={t("layout.notifications")}
                >
                    <Bell size={14} />
                    {notificationsCount > 0 && (
                        <span
                            className="absolute -top-1 -right-1 min-w-[16px] h-[16px]
                                       px-1 rounded-full v12-border-bg
                                       border v12-border
                                       text-[10px] text-neutral-100
                                       flex items-center justify-center"
                            data-testid="v12-user-topbar-notif-badge"
                        >
                            {notificationsCount > 99 ? "99+" : notificationsCount}
                        </span>
                    )}
                </button>

                {/* 5a-bis · Usage widget · P0 FASE E · progress bar per 5 metrici
                    con blocked state + CTA /pricing. Fetch /api/user/usage on open. */}
                <UsageWidget />

                {/* 5b · Avatar dropdown · MP.7 Hotfix R6
                    Sostituisce il link diretto a /profile con dropdown
                    menu che espone 8 shortcut account (ex-cluster sidebar
                    collassato in R5 e ora rimosso del tutto in R6) +
                    divider + logout. Il logout icon separato qui sotto
                    resta per fast-access UX. */}
                <V12UserAvatarMenu />

                {/* 5c · Logout */}
                <button
                    type="button"
                    onClick={logout}
                    className="inline-flex items-center justify-center
                               w-9 h-9 rounded-md border v12-border
                               v12-card text-neutral-400
                               hover:border-[#CCCCCC]/30 hover:text-neutral-100
                               transition-colors duration-200"
                    data-testid="v12-user-topbar-logout-button"
                    aria-label={t("user_dashboard.logout") !== "user_dashboard.logout"
                        ? t("user_dashboard.logout") : "Esci"}
                    title={t("user_dashboard.logout") !== "user_dashboard.logout"
                        ? t("user_dashboard.logout") : "Esci"}
                >
                    <LogOut size={14} />
                </button>

                {/* Phase VI.5 Wave A · R2 Issue 5 · "Versione classica"
                    escape link removed from the user topbar. Policy 2a
                    (HIDE stub) extended: no classic escape visible to the
                    end user. The route /dashboard/legacy still works for
                    power users typing the URL manually. */}
            </div>
        </header>
    );
}
